import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface FileDialogInsertData {
  url: string;
  text: string;
}

export interface FileDialogProps {
  open: boolean;
  initialUrl?: string;
  initialText?: string;
  theme?: string;
  onInsert: (data: FileDialogInsertData) => void;
  onCancel: () => void;
}

export function FileDialog({
  open,
  initialUrl = '',
  initialText = '',
  theme: themeProp,
  onInsert,
  onCancel,
}: FileDialogProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [urlError, setUrlError] = useState('');

  const urlInputRef = useRef<HTMLInputElement>(null);

  const validateUrl = useCallback((value: string): string => {
    if (!value.trim()) {
      return '';
    }
    const urlPattern = /^(https?:\/\/|\/)/i;
    if (!urlPattern.test(value.trim()) && value.indexOf('.') === -1) {
      return 'Please enter a valid file URL';
    }
    return '';
  }, []);

  const isValid = useMemo(() => {
    return url.trim().length > 0 && !urlError;
  }, [url, urlError]);

  const suggestedText = useMemo(() => {
    if (text.trim()) return text;
    if (!url) return '';
    try {
      const parts = url.split('/');
      return parts[parts.length - 1] || url;
    } catch {
      return url;
    }
  }, [text, url]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setText(initialText);
      setUrlError('');
    }
  }, [open, initialUrl, initialText]);

  // Focus URL input when dialog opens
  useEffect(() => {
    if (open) {
      // Defer focus to next frame so the input is rendered
      const raf = requestAnimationFrame(() => {
        urlInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  // Validate URL on change
  useEffect(() => {
    setUrlError(validateUrl(url));
  }, [url, validateUrl]);

  const handleInsert = useCallback(() => {
    if (!isValid) return;

    let finalUrl = url.trim();
    if (!finalUrl.match(/^(https?:\/\/|\/)/i)) {
      finalUrl = 'https://' + finalUrl;
    }

    onInsert({
      url: finalUrl,
      text: text.trim() || suggestedText,
    });
    onCancel();
  }, [isValid, url, text, suggestedText, onInsert, onCancel]);

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleInsert();
      }
    },
    [handleClose, handleInsert]
  );

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!open) return null;

  const dialogClasses = [
    'kst-dialog',
    'kst-file-dialog',
    theme || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="kst-dialog-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeydown}
    >
      <div className={dialogClasses} role="dialog" aria-labelledby="file-dialog-title">
        <div className="kst-dialog-header">
          <h2 id="file-dialog-title">Insert File Link</h2>
          <button className="kst-dialog-close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="kst-dialog-body">
          <div className="kst-form-group">
            <label htmlFor="file-url">
              File URL <span className="required">*</span>
            </label>
            <input
              ref={urlInputRef}
              id="file-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/document.pdf"
              className={urlError ? 'has-error' : ''}
            />
            {urlError && <span className="error-message">{urlError}</span>}
          </div>
          <div className="kst-form-group">
            <label htmlFor="file-text">Display Text</label>
            <input
              id="file-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={suggestedText || 'Link text'}
            />
          </div>
        </div>
        <div className="kst-dialog-footer">
          <button className="kst-btn kst-btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="kst-btn kst-btn-primary"
            onClick={handleInsert}
            disabled={!isValid}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).KstFileDialogReact = FileDialog;
